/**
 * AddMilestoneDialog Component
 *
 * Dialog for creating a new milestone under an objective
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';
import { COMPLETION_STATUS_OPTIONS, getStatusColor } from '../utils/scoreUtils';

export const AddMilestoneDialog: React.FC = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { data, dialogs, filteredObjectives } = useOKRDashboardContext();
    const { setSuccess, setError, softRefetchData, employees, userContext } = data;
    const {
        dialogs: dialogState,
        formState,
        closeAddMilestoneDialog,
    } = dialogs;

    const preSelectedObjectiveId = formState.selectedTeamForMilestone;

    // Local state for form fields
    const [title, setTitle] = useState('');
    const [startDate, setStartDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [assignedTo, setAssignedTo] = useState('');
    const [status, setStatus] = useState('not_started');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedObjectiveId, setSelectedObjectiveId] = useState('');

    // Get individual objectives from team objectives for the dropdown
    // Filter to selected employee's objectives when coming from Team Members
    const deptTeamObjs = filteredObjectives.myDeptTeam ?? filteredObjectives.team;
    const availableObjectives = useMemo(() => {
        const objectives: Array<{ id: string; title: string; employeeName: string; teamTitle: string; employeeId: string }> = [];
        deptTeamObjs.forEach((teamObj) => {
            (teamObj.individualObjectives || []).forEach((indObj) => {
                objectives.push({
                    id: indObj.id,
                    title: indObj.title,
                    employeeName: indObj.employee_name || 'Unknown',
                    teamTitle: teamObj.title,
                    employeeId: indObj.employee_id,
                });
            });
        });

        // When opened from Team Members, filter to only show that employee's objectives
        const selectedMemberId = formState.selectedMemberForMilestone;
        if (selectedMemberId) {
            return objectives.filter(obj => obj.employeeId === selectedMemberId);
        }
        return objectives;
    }, [deptTeamObjs, formState.selectedMemberForMilestone]);

    // Determine the effective objectiveId (pre-selected or user-selected)
    const objectiveId = preSelectedObjectiveId || selectedObjectiveId;

    // Pre-populate assignedTo when dialog opens with a member ID
    useEffect(() => {
        if (dialogState.addMilestoneDialog) {
            setAssignedTo(formState.selectedMemberForMilestone || '');
            setSelectedObjectiveId('');
        }
    }, [dialogState.addMilestoneDialog, formState.selectedMemberForMilestone]);

    const handleSubmit = async () => {
        if (!objectiveId || !title.trim()) return;

        setIsSubmitting(true);
        try {
            await okrApi.createMilestone(
                objectiveId,
                title.trim(),
                dueDate || undefined,
                startDate || undefined,
                assignedTo || undefined,
                status
            );
            setSuccess('Milestone created successfully');
            if (softRefetchData) {
                await softRefetchData();
            }
            handleClose();
        } catch (err: any) {
            setError(err.message || 'Failed to create milestone');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSubmit();
        }
    };

    const handleClose = () => {
        closeAddMilestoneDialog();
        setTitle('');
        setStartDate('');
        setDueDate('');
        setAssignedTo('');
        setStatus('not_started');
        setSelectedObjectiveId('');
    };

    return (
        <Dialog
            open={dialogState.addMilestoneDialog}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#fff',
                    borderRadius: isMobile ? 0 : 3,
                    border: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    minHeight: isMobile ? '100vh' : 520,
                    maxHeight: isMobile ? '100vh' : '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    pb: 2,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Add sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    Add Milestone
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 3, pb: 2, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    {/* Objective selector - shown when no objective is pre-selected */}
                    {!preSelectedObjectiveId && availableObjectives.length > 0 && (
                        <FormControl fullWidth required>
                            <InputLabel>Select Objective</InputLabel>
                            <Select
                                value={selectedObjectiveId}
                                onChange={(e) => setSelectedObjectiveId(e.target.value)}
                                label="Select Objective"
                                sx={{
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                }}
                            >
                                <MenuItem value="">
                                    <em>Select an objective...</em>
                                </MenuItem>
                                {availableObjectives.map((obj) => (
                                    <MenuItem key={obj.id} value={obj.id}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {obj.title}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                                                {obj.employeeName} - {obj.teamTitle}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {/* Message when employee has no objectives */}
                    {!preSelectedObjectiveId && availableObjectives.length === 0 && (
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: isDarkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                border: '1px solid',
                                borderColor: '#f59e0b',
                            }}
                        >
                            <Typography color="warning.main" variant="body2" sx={{ fontWeight: 500 }}>
                                This employee has no objectives yet.
                            </Typography>
                            <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                                Please create an individual objective for this team member first before assigning tasks.
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        label="Milestone Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        required
                        autoFocus={!!preSelectedObjectiveId}
                        placeholder="Enter milestone title..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                },
                            }}
                        />
                        <TextField
                            label="Due Date"
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                },
                            }}
                        />
                    </Box>
                    <FormControl fullWidth>
                        <InputLabel>Assigned To</InputLabel>
                        <Select
                            value={assignedTo}
                            onChange={(e) => setAssignedTo(e.target.value)}
                            label="Assigned To"
                            sx={{
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            }}
                        >
                            <MenuItem value="">
                                <em>Unassigned</em>
                            </MenuItem>
                            {userContext?.id && (
                                <MenuItem value={userContext.id}>
                                    <strong>Assign to me</strong>
                                </MenuItem>
                            )}
                            {employees?.filter((emp: any) =>
                                (emp.emp_id || emp.id) !== userContext?.id &&
                                Number(emp.dept_id || emp.department_id) === Number(userContext?.dept_id)
                            ).map((emp: any) => (
                                <MenuItem key={emp.emp_id || emp.id} value={emp.emp_id || emp.id}>
                                    {emp.name || `${emp.first_name} ${emp.last_name}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            label="Status"
                            sx={{
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                color: getStatusColor(status),
                                fontWeight: 600,
                            }}
                        >
                            {COMPLETION_STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ color: opt.color }}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography
                        variant="caption"
                        sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                    >
                        Tip: Press Ctrl+Enter to save quickly
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                }}
            >
                <Button
                    onClick={handleClose}
                    sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!objectiveId || !title.trim() || isSubmitting}
                    sx={{
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                        },
                    }}
                >
                    {isSubmitting ? 'Creating...' : 'Add Milestone'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddMilestoneDialog;
